import { useState, useEffect, useMemo } from 'react';
import Modal from '../Modal';
import { PlayIcon } from '@heroicons/react/24/outline';
import { useGraphQL } from '../../graphql';

const StartWorkflowModal = ({ isOpen, onClose, template, onStart }) => {
  const { executeQuery, queries } = useGraphQL();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);

  // Detect if we need to load companies or users based on parameter names
  const { hasCompanyParam, hasUserParam } = useMemo(() => {
    if (!template?.inputSchema) return { hasCompanyParam: false, hasUserParam: false };

    const hasCompany = template.inputSchema.some(
      p => p.name.toLowerCase() === 'companyid'
    );
    const hasUser = template.inputSchema.some(
      p => p.name.toLowerCase() === 'userid'
    );

    return { hasCompanyParam: hasCompany, hasUserParam: hasUser };
  }, [template]);

  // Load companies/users when modal opens and we need them
  useEffect(() => {
    if (!isOpen) return;

    const loadEntities = async () => {
      setEntitiesLoading(true);
      try {
        const promises = [];

        if (hasCompanyParam && companies.length === 0) {
          promises.push(
            executeQuery(queries.getCompanies(), { limit: 200, offset: 0 })
              .then(data => setCompanies(data.adminCompanies || []))
          );
        }

        if (hasUserParam && users.length === 0) {
          promises.push(
            executeQuery(queries.getUsers(), { limit: 200, offset: 0 })
              .then(data => setUsers(data.adminUsers || []))
          );
        }

        await Promise.all(promises);
      } catch (err) {
        console.error('Failed to load entities:', err);
      } finally {
        setEntitiesLoading(false);
      }
    };

    if (hasCompanyParam || hasUserParam) {
      loadEntities();
    }
  }, [isOpen, hasCompanyParam, hasUserParam]);

  // Initialize form with default values when template changes
  useEffect(() => {
    if (!template?.inputSchema) {
      setFormData({});
      return;
    }
    const defaults = {};
    template.inputSchema.forEach(param => {
      if (param.default !== null && param.default !== undefined) {
        defaults[param.name] = param.default;
      }
    });
    setFormData(defaults);
  }, [template]);

  const handleChange = (name, value, type) => {
    let parsedValue = value;
    if (type === 'int') parsedValue = parseInt(value, 10) || 0;
    else if (type === 'bool') parsedValue = value === 'true';
    else if (type === 'float') parsedValue = parseFloat(value) || 0;

    setFormData(prev => ({ ...prev, [name]: parsedValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onStart(formData);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  // Check if a param is a companyId or userId field
  const isCompanyIdParam = (name) => name.toLowerCase() === 'companyid';
  const isUserIdParam = (name) => name.toLowerCase() === 'userid';

  // Render the appropriate input for a parameter
  const renderParamInput = (param) => {
    // Boolean type - always use select
    if (param.type === 'bool') {
      return (
        <select
          value={String(formData[param.name] ?? param.default ?? false)}
          onChange={(e) => handleChange(param.name, e.target.value, 'bool')}
          className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white font-mono text-sm focus:border-white focus:outline-none"
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    }

    // Company ID - show company dropdown
    if (isCompanyIdParam(param.name) && companies.length > 0) {
      return (
        <select
          value={formData[param.name] ?? ''}
          onChange={(e) => handleChange(param.name, e.target.value, 'string')}
          required={param.required}
          className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white font-mono text-sm focus:border-white focus:outline-none"
        >
          <option value="">-- Select Company --</option>
          {companies.map(company => (
            <option key={company.id} value={company.id}>
              {company.name} ({company.size})
            </option>
          ))}
        </select>
      );
    }

    // User ID - show user dropdown
    if (isUserIdParam(param.name) && users.length > 0) {
      return (
        <select
          value={formData[param.name] ?? ''}
          onChange={(e) => handleChange(param.name, e.target.value, 'string')}
          required={param.required}
          className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white font-mono text-sm focus:border-white focus:outline-none"
        >
          <option value="">-- Select User --</option>
          {users.map(user => (
            <option key={user.id} value={user.id}>
              {user.firstName} {user.lastName} ({user.email})
            </option>
          ))}
        </select>
      );
    }

    // Default: text/number input
    return (
      <input
        type={param.type === 'int' || param.type === 'float' ? 'number' : 'text'}
        value={formData[param.name] ?? ''}
        onChange={(e) => handleChange(param.name, e.target.value, param.type)}
        placeholder={param.description}
        required={param.required}
        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white font-mono text-sm focus:border-white focus:outline-none"
      />
    );
  };

  if (!template) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`START: ${template.name.toUpperCase()}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-400 font-mono">
          {template.description}
        </p>

        {/* Loading indicator for entities */}
        {entitiesLoading && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <div className="animate-spin h-3 w-3 border border-gray-400 border-t-transparent rounded-full" />
            Loading companies/users...
          </div>
        )}

        {template.inputSchema?.length > 0 ? (
          template.inputSchema.map(param => (
            <div key={param.name}>
              <label className="block text-xs font-mono text-gray-400 uppercase mb-1">
                {param.name}
                {param.required && <span className="text-red-500 ml-1">*</span>}
                {/* Show hint for smart dropdowns */}
                {(isCompanyIdParam(param.name) && companies.length > 0) && (
                  <span className="text-blue-700 ml-2 normal-case">(select from list)</span>
                )}
                {(isUserIdParam(param.name) && users.length > 0) && (
                  <span className="text-blue-700 ml-2 normal-case">(select from list)</span>
                )}
              </label>

              {renderParamInput(param)}

              {param.description && (
                <p className="text-xs text-gray-600 mt-1">{param.description}</p>
              )}
            </div>
          ))
        ) : (
          <p className="text-xs text-gray-500 font-mono">This workflow has no input parameters.</p>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t border-gray-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 text-gray-400 font-mono text-xs font-bold hover:bg-gray-700 uppercase"
          >
            CANCEL
          </button>
          <button
            type="submit"
            disabled={loading || entitiesLoading}
            className="px-4 py-2 bg-green-600 text-white font-mono text-xs font-bold hover:bg-green-500 disabled:opacity-50 flex items-center gap-2 uppercase"
          >
            <PlayIcon className="w-4 h-4" />
            {loading ? 'STARTING...' : 'START WORKFLOW'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default StartWorkflowModal;
